import { useState, useMemo } from 'react';
import { Trans, Plural, useLingui } from '@lingui/react/macro';
import { useNavigate } from '@tanstack/react-router';
import { Card, CardContent, cn } from '@pantry/ui';
import { type ItemWithInstanceCounts, type StorageLocation } from '@pantry/api-client';
import {
  type Household,
  type InventorySortBy,
  type SortOrder,
  type ConsolidatedItem,
  consolidateItems,
  partitionByVisibility,
  sortItems,
} from '@pantry/shared';
import { ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { ItemCard } from './item-card';
import { ConsolidatedItemCard } from './consolidated-item-card';
import { LocationGroup } from './location-group';

/** Compute the navigation route for a consolidated item. */
type ConsolidatedItemRoute =
  | { to: '/items/groups/$groupId'; params: { groupId: string } }
  | { to: '/items/$itemId'; params: { itemId: string } }
  | null;

/** Compute the type-safe navigation route for a consolidated item. */
function getConsolidatedItemRoute(consolidated: ConsolidatedItem): ConsolidatedItemRoute {
  if (consolidated.groupId) {
    return { to: '/items/groups/$groupId', params: { groupId: consolidated.groupId } };
  }
  const itemId = consolidated.items[0]?.id;
  if (itemId) {
    return { to: '/items/$itemId', params: { itemId } };
  }
  return null;
}

/**
 * Storage location type to icon mapping.
 */
function getLocationIcon(type: string) {
  switch (type) {
    case 'fridge':
      return '🧊';
    case 'freezer':
      return '❄️';
    case 'pantry':
      return '🥫';
    default:
      return '📦';
  }
}

/**
 * Flat list of all items sorted and consolidated.
 */
export function FlatItemsList({
  items,
  householdId,
  household,
  sortBy,
  sortOrder,
  getDisplayName,
  onSetTarget,
  onAddToGroup,
}: {
  items: ItemWithInstanceCounts[];
  householdId: string;
  household: Household | null;
  sortBy: InventorySortBy;
  sortOrder: SortOrder;
  getDisplayName: (item: ItemWithInstanceCounts) => string;
  onSetTarget?: (consolidated: ConsolidatedItem) => void;
  onAddToGroup?: (consolidated: ConsolidatedItem) => void;
}) {
  const navigate = useNavigate();
  const [expandedConsolidated, setExpandedConsolidated] = useState<Set<string>>(new Set());

  const toggleConsolidated = (key: string) => {
    setExpandedConsolidated((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const sortedItems = useMemo(
    () => sortItems(items, sortBy, sortOrder, getDisplayName),
    [items, sortBy, sortOrder, getDisplayName]
  );

  const consolidatedItems = useMemo(() => consolidateItems(sortedItems), [sortedItems]);
  const { visible, depleted, needsRestocking } = useMemo(
    () => partitionByVisibility(consolidatedItems),
    [consolidatedItems]
  );

  return (
    <div className="space-y-4">
      {/* Needs Restocking */}
      {needsRestocking.length > 0 && (
        <Card className="border-expiring-soon/30">
          <CardContent className="p-4">
            <p className="text-xs font-medium text-expiring-soon uppercase tracking-wider mb-3">
              <Trans>Needs Restocking ({needsRestocking.length})</Trans>
            </p>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {needsRestocking.map((consolidated) => {
                const key = `restock-${consolidated.groupId || consolidated.productId || consolidated.items[0]?.id || consolidated.displayName}`;
                return (
                  <ConsolidatedItemCard
                    key={key}
                    consolidated={consolidated}
                    isExpanded={expandedConsolidated.has(key)}
                    onToggle={() => toggleConsolidated(key)}
                    onNavigate={() => {
                      const route = getConsolidatedItemRoute(consolidated);
                      if (route) navigate(route);
                    }}
                    householdId={householdId}
                    household={household}
                    onSetTarget={onSetTarget}
                    onAddToGroup={onAddToGroup}
                  />
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* In Stock */}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {visible.map((consolidated) => {
          const key = `flat-${consolidated.groupId || consolidated.productId || consolidated.items[0]?.id || consolidated.displayName}`;
          const isConsolidatedExpanded = expandedConsolidated.has(key);

          if (consolidated.items.length === 1 && !consolidated.groupId && !consolidated.productId) {
            return (
              <ItemCard
                key={consolidated.items[0].id}
                item={consolidated.items[0]}
                householdId={householdId}
                household={household}
              />
            );
          }

          return (
            <ConsolidatedItemCard
              key={key}
              consolidated={consolidated}
              isExpanded={isConsolidatedExpanded}
              onToggle={() => toggleConsolidated(key)}
              onNavigate={() => {
                const route = getConsolidatedItemRoute(consolidated);
                if (route) navigate(route);
              }}
              householdId={householdId}
              household={household}
              onSetTarget={onSetTarget}
              onAddToGroup={onAddToGroup}
            />
          );
        })}
      </div>

      {/* Depleted */}
      {depleted.length > 0 && (
        <Card className="border-dashed border-muted-foreground/30">
          <button
            onClick={() => toggleConsolidated('__depleted_section')}
            className="flex w-full items-center justify-between p-4 text-left hover:bg-muted/30 transition-colors"
          >
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-muted/50 text-xl">
                📦
              </div>
              <div>
                <h3 className="font-heading font-semibold text-muted-foreground">
                  <Trans>Depleted</Trans>
                </h3>
                <p className="text-sm text-muted-foreground">
                  <Plural
                    value={depleted.length}
                    one="# item at zero stock"
                    other="# items at zero stock"
                  />
                </p>
              </div>
            </div>
            <div
              className={cn(
                'flex h-8 w-8 items-center justify-center rounded-lg transition-colors',
                expandedConsolidated.has('__depleted_section') ? 'bg-muted' : 'bg-transparent'
              )}
            >
              {expandedConsolidated.has('__depleted_section') ? (
                <ChevronDownIcon className="h-5 w-5 text-muted-foreground" />
              ) : (
                <ChevronRightIcon className="h-5 w-5 text-muted-foreground" />
              )}
            </div>
          </button>
          {expandedConsolidated.has('__depleted_section') && (
            <CardContent className="pt-0 pb-4 border-t border-border/50">
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 opacity-60">
                {depleted.map((consolidated) => {
                  const key = `depleted-${consolidated.groupId || consolidated.productId || consolidated.items[0]?.id || consolidated.displayName}`;
                  return (
                    <ConsolidatedItemCard
                      key={key}
                      consolidated={consolidated}
                      isExpanded={expandedConsolidated.has(key)}
                      onToggle={() => toggleConsolidated(key)}
                      onNavigate={() => {
                        const route = getConsolidatedItemRoute(consolidated);
                        if (route) navigate(route);
                      }}
                      householdId={householdId}
                      household={household}
                      onSetTarget={onSetTarget}
                      onAddToGroup={onAddToGroup}
                    />
                  );
                })}
              </div>
            </CardContent>
          )}
        </Card>
      )}
    </div>
  );
}

/**
 * Group items by storage location and render collapsible sections.
 */
export function GroupedItemsList({
  items,
  storageLocations,
  householdId,
  household,
  sortBy,
  sortOrder,
  getDisplayName,
  onSetTarget,
  onAddToGroup,
}: {
  items: ItemWithInstanceCounts[];
  storageLocations: StorageLocation[];
  householdId: string;
  household: Household | null;
  sortBy: InventorySortBy;
  sortOrder: SortOrder;
  getDisplayName: (item: ItemWithInstanceCounts) => string;
  onSetTarget?: (consolidated: ConsolidatedItem) => void;
  onAddToGroup?: (consolidated: ConsolidatedItem) => void;
}) {
  const { t } = useLingui();
  const navigate = useNavigate();
  // Track which groups are expanded (all expanded by default)
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(
    () => new Set(['unassigned', ...storageLocations.map((l) => l.id)])
  );

  // Track which consolidated items are expanded to show individual items
  const [expandedConsolidated, setExpandedConsolidated] = useState<Set<string>>(new Set());

  // Group items by storage location ID
  const groupedItems = useMemo(() => {
    const groups = new Map<string | null, ItemWithInstanceCounts[]>();

    for (const item of items) {
      const locationId = item.storageLocationId;
      let group = groups.get(locationId);
      if (!group) {
        group = [];
        groups.set(locationId, group);
      }
      group.push(item);
    }

    return groups;
  }, [items]);

  // Build ordered list of groups (sorted locations first, then unassigned)
  const sortedLocations = useMemo(() => {
    return [...storageLocations].sort((a, b) => a.sortOrder - b.sortOrder);
  }, [storageLocations]);

  const toggleGroup = (groupId: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupId)) {
        next.delete(groupId);
      } else {
        next.add(groupId);
      }
      return next;
    });
  };

  const toggleConsolidated = (key: string) => {
    setExpandedConsolidated((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  // Items without a storage location
  const unassignedItems = groupedItems.get(null) || [];

  // Consolidate ALL items to extract depleted/restocking across locations
  const allConsolidated = useMemo(() => consolidateItems(items), [items]);
  const { depleted, needsRestocking } = useMemo(
    () => partitionByVisibility(allConsolidated),
    [allConsolidated]
  );

  // Build a set of item IDs in depleted/restocking to exclude from location groups
  const excludedItemIds = useMemo(() => {
    const ids = new Set<string>();
    for (const c of [...depleted, ...needsRestocking]) {
      for (const item of c.items) ids.add(item.id);
    }
    return ids;
  }, [depleted, needsRestocking]);

  // IDs of depleted/restocking consolidated entries (for zero-stock items with empty items[])
  const excludedProductIds = useMemo(() => {
    const ids = new Set<string>();
    for (const c of [...depleted, ...needsRestocking]) {
      if (c.productId) ids.add(c.productId);
    }
    return ids;
  }, [depleted, needsRestocking]);

  /** Render a consolidated card or single item card */
  const renderConsolidated = (consolidated: ConsolidatedItem, keyPrefix: string) => {
    const key = `${keyPrefix}-${consolidated.groupId || consolidated.productId || consolidated.items[0]?.id || consolidated.displayName}`;
    const isConsolidatedExpanded = expandedConsolidated.has(key);

    if (consolidated.items.length === 1 && !consolidated.groupId && !consolidated.productId) {
      return (
        <ItemCard
          key={consolidated.items[0].id}
          item={consolidated.items[0]}
          householdId={householdId}
          household={household}
        />
      );
    }

    return (
      <ConsolidatedItemCard
        key={key}
        consolidated={consolidated}
        isExpanded={isConsolidatedExpanded}
        onToggle={() => toggleConsolidated(key)}
        onNavigate={() => {
          const route = getConsolidatedItemRoute(consolidated);
          if (route) navigate(route);
        }}
        householdId={householdId}
        household={household}
        onSetTarget={onSetTarget}
        onAddToGroup={onAddToGroup}
      />
    );
  };

  /** Filter out items that belong to depleted/restocking sections */
  const filterInStock = (itemsList: ItemWithInstanceCounts[]) =>
    itemsList.filter(
      (item) => !excludedItemIds.has(item.id) && !excludedProductIds.has(item.productId ?? '')
    );

  return (
    <div className="space-y-4">
      {/* Needs Restocking — above location groups */}
      {needsRestocking.length > 0 && (
        <Card className="border-expiring-soon/30">
          <CardContent className="p-4">
            <p className="text-xs font-medium text-expiring-soon uppercase tracking-wider mb-3">
              <Trans>Needs Restocking ({needsRestocking.length})</Trans>
            </p>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {needsRestocking.map((c) => renderConsolidated(c, 'restock'))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Render each storage location group (in-stock items only) */}
      {sortedLocations.map((location) => {
        const locationItems = groupedItems.get(location.id) || [];
        const inStockItems = filterInStock(locationItems);
        if (inStockItems.length === 0) return null;

        const isExpanded = expandedGroups.has(location.id);
        const sortedLocationItems = sortItems(inStockItems, sortBy, sortOrder, getDisplayName);
        const consolidatedItems = consolidateItems(sortedLocationItems);

        return (
          <LocationGroup
            key={location.id}
            title={location.name}
            icon={getLocationIcon(location.type)}
            itemCount={inStockItems.length}
            isExpanded={isExpanded}
            onToggle={() => toggleGroup(location.id)}
          >
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {consolidatedItems.map((c) => renderConsolidated(c, location.id))}
            </div>
          </LocationGroup>
        );
      })}

      {/* Unassigned items (in-stock only) */}
      {(() => {
        const inStockUnassigned = filterInStock(unassignedItems);
        if (inStockUnassigned.length === 0) return null;

        const sortedUnassigned = sortItems(inStockUnassigned, sortBy, sortOrder, getDisplayName);
        const consolidatedItems = consolidateItems(sortedUnassigned);
        return (
          <LocationGroup
            title={t`Unassigned`}
            icon="📍"
            itemCount={inStockUnassigned.length}
            isExpanded={expandedGroups.has('unassigned')}
            onToggle={() => toggleGroup('unassigned')}
            muted
          >
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {consolidatedItems.map((c) => renderConsolidated(c, 'unassigned'))}
            </div>
          </LocationGroup>
        );
      })()}

      {/* Depleted — below location groups */}
      {depleted.length > 0 && (
        <Card className="border-dashed border-muted-foreground/30">
          <button
            onClick={() => toggleConsolidated('__depleted_grouped')}
            className="flex w-full items-center justify-between p-4 text-left hover:bg-muted/30 transition-colors"
          >
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-muted/50 text-xl">
                📦
              </div>
              <div>
                <h3 className="font-heading font-semibold text-muted-foreground">
                  <Trans>Depleted</Trans>
                </h3>
                <p className="text-sm text-muted-foreground">
                  <Plural
                    value={depleted.length}
                    one="# item at zero stock"
                    other="# items at zero stock"
                  />
                </p>
              </div>
            </div>
            <div
              className={cn(
                'flex h-8 w-8 items-center justify-center rounded-lg transition-colors',
                expandedConsolidated.has('__depleted_grouped') ? 'bg-muted' : 'bg-transparent'
              )}
            >
              {expandedConsolidated.has('__depleted_grouped') ? (
                <ChevronDownIcon className="h-5 w-5 text-muted-foreground" />
              ) : (
                <ChevronRightIcon className="h-5 w-5 text-muted-foreground" />
              )}
            </div>
          </button>
          {expandedConsolidated.has('__depleted_grouped') && (
            <CardContent className="pt-0 pb-4 border-t border-border/50">
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 opacity-60">
                {depleted.map((c) => renderConsolidated(c, 'depleted'))}
              </div>
            </CardContent>
          )}
        </Card>
      )}
    </div>
  );
}
